import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ProgramSelect from '@/components/ProgramSelect/ProgramSelect';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import {
  ProgramAndPhaseFormPayload,
  ProgramAndPhaseFormValues,
  Props
} from '@/forms/ProgramAndPhaseForm/types';
import PersonContext from '@/pages/Person/PersonContext';
import PhaseRepository from '@/repositories/PhaseRepository';
import { Alert, Button, Card, Checkbox, DualTime, InputGroup, Typography } from '@/ui';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const ProgramAndPhaseForm = ({ episodeOfCare, onCancel, onSuccess, personId }: Props) => {
  const { addMessage } = useContext(AppContext);
  const {
    person: { time_zone: memberTZ }
  } = useContext(PersonContext);
  const queryClient = useQueryClient();
  const phaseRepository = PhaseRepository();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [selectedProgramName, setSelectedProgramName] = useState<string>('');
  const [selectedPhaseTypeName, setSelectedPhaseTypeName] = useState<string>('');

  const phaseStartAt = episodeOfCare.current_active_phase?.start_at
    ? new Date(episodeOfCare.current_active_phase.start_at)
    : null;

  const initialValues: ProgramAndPhaseFormValues = {
    primary_substance: '',
    program_id: ''
  };

  const {
    formState: { errors },
    control,
    handleSubmit,
    register,
    setValue,
    watch
  } = useForm({ defaultValues: initialValues });

  const selectedPrimarySubstance = watch('primary_substance');
  const selectedProgramId = watch('program_id');
  const isSelectionComplete = Boolean(
    selectedPrimarySubstance && selectedProgramId && selectedPhaseTypeName
  );

  const updateProgramAndPhase = useMutation({
    mutationFn: ({ values }: { values: ProgramAndPhaseFormPayload }) => {
      return phaseRepository.update({
        id: episodeOfCare.current_active_phase!.id,
        data: { episode_of_care: episodeOfCare.id, ...values }
      });
    },
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Successfully updated phase for episode of care' });
      personId && queryClient.invalidateQueries({ queryKey: ['getPerson', personId] });
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: ProgramAndPhaseFormValues) => {
    updateProgramAndPhase.mutate({ values });
  };

  const onPrimarySubstanceChange = useCallback(
    (primarySubstance: string | null) => setValue('primary_substance', primarySubstance ?? ''),
    [setValue]
  );

  const onProgramChange = useCallback(
    (program: string | null, programName: string | undefined, phaseTypeName: string) => {
      setValue('program_id', program ?? '');
      setSelectedProgramName(programName || '');
      setSelectedPhaseTypeName(phaseTypeName);
    },
    [setValue]
  );

  register('primary_substance', {
    required: 'Primary Concern is required'
  });

  useEffect(() => {
    if (!isSelectionComplete && isConfirmed) {
      setIsConfirmed(false);
    }
  }, [isConfirmed, isSelectionComplete]);

  return (
    <form
      onSubmit={handleSubmit(submitForm)}
      data-testid="form-update-program-and-phase"
      className="pt-4">
      <InputGroup>
        <FieldError message={errors.primary_substance?.message} />
        <Controller
          name="program_id"
          control={control}
          render={() => {
            return (
              <ProgramSelect
                onPrimarySubstanceChange={onPrimarySubstanceChange}
                onProgramChange={onProgramChange}
              />
            );
          }}
          rules={{
            required: 'Program is required'
          }}
        />

        <FieldError message={errors.program_id?.message} />
      </InputGroup>

      {phaseStartAt && (
        <InputGroup>
          <Card padding="xs" className="bg-uniform-gray-50">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <FontAwesomeIcon
                icon={faLock}
                className="shrink-0 text-uniform-gray-500"
                aria-hidden="true"
              />
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Typography variant="label" muted className="mb-0">
                  Start date
                </Typography>
                <DualTime
                  dateTime={phaseStartAt}
                  memberTZ={memberTZ ?? undefined}
                  tooltipText="Member's local time"
                />
              </div>
            </div>
          </Card>
        </InputGroup>
      )}

      {isSelectionComplete && (
        <Alert variant="warning" className="mb-4">
          <Checkbox
            checked={isConfirmed}
            color="warning"
            id="program-correction-confirmation"
            onChange={setIsConfirmed}
            label={
              <span className="text-sm text-amber-900">
                I confirm that this member has been in the{' '}
                <strong>{selectedProgramName || 'the selected'}</strong> program (
                <strong>{selectedPhaseTypeName}</strong> phase) since{' '}
                <strong>{phaseStartAt ? phaseStartAt.toLocaleDateString() : 'admission'}</strong>.
                This is a <strong>correction</strong> to a data-entry error, not a new phase
                transition.
              </span>
            }
          />
        </Alert>
      )}

      <InputGroup>
        <div className="flex items-center gap-2">
          <Button
            color="destructive"
            disabled={!isConfirmed || updateProgramAndPhase.isPending}
            isLoading={updateProgramAndPhase.isPending}
            type="submit">
            Issue correction
          </Button>
          {onCancel && (
            <Button variant="outline" color="neutral" onClick={onCancel} type="button">
              Cancel
            </Button>
          )}
        </div>
      </InputGroup>
    </form>
  );
};

export default ProgramAndPhaseForm;
